import { Geometry, Position } from "geojson";
import { LatLngLiteral } from "leaflet";

/** Anéis de cada polígono da geometria. Posição GeoJSON é [lng, lat]. */
const poligonosDe = (geom: Geometry): Position[][][] => {
  if (geom.type === "Polygon") return [geom.coordinates];
  if (geom.type === "MultiPolygon") return geom.coordinates;
  return [];
};

/** Área planar do anel, em graus quadrados. Serve só para comparar tamanhos. */
const areaAnel = (anel: Position[]): number => {
  let dobro = 0;
  for (let i = 0, j = anel.length - 1; i < anel.length; j = i++) {
    dobro += anel[j][0] * anel[i][1] - anel[i][0] * anel[j][1];
  }
  return Math.abs(dobro) / 2;
};

const centroideAnel = (anel: Position[]): { x: number; y: number } | null => {
  let dobro = 0;
  let x = 0;
  let y = 0;
  for (let i = 0, j = anel.length - 1; i < anel.length; j = i++) {
    const f = anel[j][0] * anel[i][1] - anel[i][0] * anel[j][1];
    dobro += f;
    x += (anel[j][0] + anel[i][0]) * f;
    y += (anel[j][1] + anel[i][1]) * f;
  }
  if (!dobro) return null;
  return { x: x / (3 * dobro), y: y / (3 * dobro) };
};

/**
 * Longitudes onde a reta horizontal `lat` corta as bordas do polígono. Com os
 * buracos na mesma lista, os cruzamentos ordenados se emparelham em intervalos
 * que estão DENTRO do polígono (regra par-ímpar): [0,1], [2,3], e assim por
 * diante. A comparação `(y1 > lat) !== (y2 > lat)` conta cada vértice uma vez
 * só, senão um vértice exatamente na linha inverteria a paridade.
 */
const cruzamentos = (aneis: Position[][], lat: number): number[] => {
  const xs: number[] = [];
  for (const anel of aneis) {
    for (let i = 0, j = anel.length - 1; i < anel.length; j = i++) {
      const [x1, y1] = anel[j];
      const [x2, y2] = anel[i];
      if (y1 > lat !== y2 > lat) {
        xs.push(x1 + ((lat - y1) / (y2 - y1)) * (x2 - x1));
      }
    }
  }
  return xs.sort((a, b) => a - b);
};

/**
 * Ponto para ancorar o rótulo, garantidamente DENTRO da geometria — o mesmo
 * que o ST_PointOnSurface do PostGIS faz, e não o centroide.
 *
 * O centroide serve para polígono convexo e mais nada: num "L", num "C" ou num
 * polígono com buraco ele cai fora da área. E o `getCenter()` do Leaflet, que
 * é o que um tooltip permanente usa sozinho, ainda por cima olha só o primeiro
 * anel do primeiro polígono — e a consulta prévia entrega UMA feição por zona,
 * com as manchas todas fundidas num MultiPolygon, então o rótulo ia parar na
 * primeira mancha da união, que não é a maior nem fica onde o cidadão olha.
 *
 * Aqui: escolhe a maior parte, corta-a com a horizontal que passa pelo
 * centroide dela e devolve o meio do trecho interno mais largo.
 */
export const pontoRotulo = (geom: Geometry): LatLngLiteral | null => {
  const poligonos = poligonosDe(geom).filter((aneis) => aneis.length > 0);
  if (!poligonos.length) return null;

  const maior = poligonos.reduce((a, b) =>
    areaAnel(b[0]) > areaAnel(a[0]) ? b : a
  );

  const centro = centroideAnel(maior[0]);
  // Anel degenerado (área zero): sem centroide, sobra o primeiro vértice.
  if (!centro) {
    const [lng, lat] = maior[0][0] ?? [];
    return lng === undefined ? null : { lat, lng };
  }

  const xs = cruzamentos(maior, centro.y);

  let melhorLargura = 0;
  let melhorLng: number | null = null;
  for (let i = 0; i + 1 < xs.length; i += 2) {
    const largura = xs[i + 1] - xs[i];
    if (largura > melhorLargura) {
      melhorLargura = largura;
      melhorLng = (xs[i] + xs[i + 1]) / 2;
    }
  }

  return melhorLng === null
    ? { lat: centro.y, lng: centro.x }
    : { lat: centro.y, lng: melhorLng };
};
